/**
 * The projection: one `PlanTemplateListItem` from the wire, one row the screen can render.
 *
 * This is the only module in the slice that knows the DTO's shape. `ui/` reads `PlanLibraryRow` and
 * never the generated model, which is what lets the wire change without a component change — and
 * what keeps every "is this field absent or is it zero" decision in one testable place.
 *
 * ── The decisions this file makes, none of which are obvious ────────────────────────────────
 *
 * **Every field on the DTO is optional.** orval marks them so because the backend's OpenAPI document
 * declares no `required` array, not because the server omits them. Treating them as present would
 * mean `item.name!` in six places; treating absence as zero would put confident figures under
 * columns that measure nothing. So absence and zero are kept apart, field by field.
 *
 * **ŚWIEŻOŚĆ names WHICH event it is dating, and that is the whole fix.** The backend leaves
 * `updatedAt` null on creation and stamps it on every change, so `null` means "nobody has edited
 * this plan". Two earlier revisions both got this wrong in opposite directions:
 *
 * · The first folded `createdAt` in silently behind `updatedAt` — so a creation date printed under a
 *   column headed ŚWIEŻOŚĆ, telling the trainer a plan had been edited on a day nobody touched it.
 * · The second dropped the fold and printed the literal word "nieedytowany" for null. Measured
 *   against the real backend on 5 August 2026, **nine of ten seeded plans have a null `updatedAt`**
 *   — including a plan created a minute earlier — so the column printed one identical string down
 *   almost its whole length and the freshness sort moved nothing.
 *
 * So the projection answers with a DISCRIMINATED activity: the event, and when it happened. Both
 * facts stay true — a never-edited plan reads "utworzony 4 dni temu", which is not a claim that it
 * was edited — and the column starts carrying information on every row rather than on one.
 *
 * `createdAt` is non-nullable on the wire, so `null` here means the response carried neither
 * timestamp: a shape the backend does not currently produce, kept as a state rather than asserted
 * away.
 *
 * **RYTM is one column and two different facts**, because a cadence figure is only meaningful for a
 * scheduling mode this build can read. A `DateBased` plan reports its weekly day count; anything
 * else reports how many cycles the plan runs for. Which one applies is decided here rather than in
 * the cell, so the column can never print a number that is not about the plan in front of it.
 *
 * **The MODE MARKER is an allow-list, and it is not the negation of the rhythm's guard.** RYTM asks
 * "may this plan put a figure under a weekly cadence", so anything that is not `DateBased` — a mode
 * this build has never heard of included — answers no. The marker asks the opposite question: "which
 * mode is this, by name". Reusing the rhythm's guard would print „Własnym tempem" over a row the
 * wire called something else entirely, which is a claim rather than a conservative reading. So the
 * marker names `SelfPaced` and nothing else, and an unrecognised mode carries no marker at all —
 * indistinguishable from a weekly plan, which is the same silence the library shows today rather
 * than a new lie.
 *
 * **`daysPerWeek` is refused outside `DateBased`, and the reason is stronger than it looks.** The
 * backend computes it as `min(densest phase's day count, 7)` — a CAP. On a weekly plan the cap
 * cannot bind, because a week has seven days. On a mode whose cycle is not a week it can and does: a
 * cycle of eleven trainings reports seven, confidently and wrongly. So the field is dropped there
 * rather than relabelled.
 *
 * **`assignedClientCount` zero IS a measurement.** Nobody following a plan is a fact about the plan,
 * unlike a missing cadence, so it stays a number and the screen renders the canon's muted dash.
 *
 * **The category is carried twice, deliberately.** `categoryKey` is the domain value — it picks the
 * chip's colour, the cover pool and the filter it answers to. `category` is the trainer's word for
 * it, resolved here so the search box matches what the trainer can see: search runs over
 * `PlanLibraryRow`, so a label produced in a cell would be unsearchable.
 *
 * No React, no DOM, no query hook, no i18next import. `t` arrives as a PARAMETER — the projection is
 * a function of the language, and that is a dependency it accepts rather than creates. Formatting —
 * plurals, dates, collation — is still the caller's.
 */

import {
  planCategoryKey,
  planCoverPair,
  schedulingModeIs,
  type PlanCategoryKey,
} from '~/entities/plan-draft';
import type { TrainWithMeModulesPlansDtosResponsesPlanTemplateListItem as PlanTemplateListItem } from '~/shared/api/generated/model';

import { searchTextOf } from './searchFold';

/**
 * What the RYTM column says about one plan.
 *
 * A discriminated pair rather than a formatted string, because `model/` holds no sentences and the
 * two variants take different plural rules in Polish. `null` when the plan has no phases at all —
 * there is no cadence to report, not a zero one.
 */
export type PlanRhythm =
  | { readonly kind: 'week'; readonly daysPerWeek: number }
  | { readonly kind: 'cycle'; readonly cycles: number };

/**
 * The last thing that happened to a plan, and when.
 *
 * A pair rather than a bare timestamp, because the two kinds take different sentences and must not
 * be confused: `edited` is "zmieniony 2 dni temu", `created` is "utworzony 2 dni temu". Which one
 * applies is decided here so the cell cannot invent it, and `at` is what the freshness sort orders
 * by — which is also what the server itself orders the library by.
 */
/**
 * How long a plan RUNS, which is the WHOLE of a template row's sub-line.
 *
 * It was the first half of it until owner decision **D26** of 4 September 2026, when the exercise
 * count came out: the inspector hero beside the list carries that figure as its first KPI, so the
 * row was spending its only secondary line repeating something a hundred pixels away. Nothing was
 * lost and `exerciseCount` below is untouched — the hero still reads it.
 *
 * KANON `Programy v2 - D3 (0.8)` writes three spellings — `8 tygodni`, `zapętlony` and `bez końca`.
 * The third is dropped (owner decision D3): nothing on this endpoint means "endless", and a plan
 * that never stops is not a shape the backend can express. What the wire does carry is a cycle
 * count and a mode, and those are exactly the two answers below.
 *
 * A discriminated value rather than a string, for the reason `PlanRhythm` above is one: the count
 * has to reach `t()` for Polish to pluralise it, and `model/` takes a key-only `translate`. The
 * UI composes the sub-line.
 *
 * `null` where the plan has no phases, which is where `rhythm` is null too — a plan with nothing
 * in it has no span to state, and printing `0 tygodni` would be a figure rather than an absence.
 */
export type PlanSpan =
  | { readonly kind: 'weeks'; readonly weeks: number }
  | { readonly kind: 'looped' };

export type PlanActivity =
  | { readonly kind: 'edited'; readonly at: string }
  | { readonly kind: 'created'; readonly at: string };

/**
 * Which scheduling mode the row says it is, when the row says anything at all.
 *
 * A named member rather than a boolean, because the field answers "which mode" and not "is it the
 * unusual one": `DateBased` is unmarked because it is the shape every plan in this library had until
 * the backend's B1, not because it is the false half of a pair. A third mode adds a member here and
 * the screen stops compiling until it has a word for it — which is the point.
 *
 * `null` covers the two silences: a weekly plan, and a mode this build cannot name.
 */
export type PlanSchedulingMarker = 'selfPaced';

/**
 * Resolve one locale key. The projection takes this rather than importing i18next, which is what
 * keeps `model/` pure and lets a test hand it a stub that THROWS on an unknown key — an echoing stub
 * would make a missing translation look like a passing test (execution ADR §7).
 */
export type TranslateKey = (key: string) => string;

export interface PlanLibraryRow {
  readonly id: number;
  readonly name: string;
  /**
   * The domain category, or `null` when the plan carries none and when it carries one this build
   * has never heard of. Drives the chip's colour, the cover's pool and the category filter.
   */
  readonly categoryKey: PlanCategoryKey | null;
  /**
   * The trainer's word for the category — "Mobilność", never the wire's `"Flexibility"`. `null` when
   * the plan carries none; the raw wire value when it is a category this app has no label for,
   * because showing the server's word beats showing nothing or a dead `plans.category.…` key.
   */
  readonly category: string | null;
  /**
   * The name and the category, folded once, for the search box to match against.
   *
   * On the ROW rather than in the filter because the filter runs on every keystroke and the
   * projection runs on a response: folding 120 rows per character was two NFD normalisations and
   * six regex passes each, for a value that had not changed since the read landed. `searchFold`
   * owns both the fold and the separator between the two fields.
   */
  readonly searchText: string;
  /** The row's 76×46 crop. Never null — the cover resolver falls back to the category's pool. */
  readonly thumbUrl: string;
  /** The same asset at hero size, for the header of the plan the trainer changed most recently. */
  readonly heroUrl: string;
  /** `null` when the plan has no phases: there is no cadence to summarise, not zero of it. */
  readonly rhythm: PlanRhythm | null;
  readonly span: PlanSpan | null;
  /**
   * The scheduling mode the row wears as a marker, or `null` for a plan that wears none — a weekly
   * one, and a mode this build has no word for. See the header for why this is not `!isWeekly`.
   */
  readonly schedulingMarker: PlanSchedulingMarker | null;
  /**
   * How many training days the plan's repeat unit holds — a WEEK for a DateBased plan, the CYCLE for
   * a self-paced one, which is one figure rather than two because a weekly plan's cycle is a week.
   * `null` when the plan has no phases, which is an absence rather than a zero.
   *
   * The only reader is the DNI facet. It is the wire's `daysPerWeek` under a name that does not
   * claim a week, because the backend computes it as `Math.min(densest phase's day count, 7)`
   * (`PlanTemplateProcessor`) for EVERY mode — the two things it is not are a week-only figure and
   * an exact one. The cap cannot bind on the facet: `maxDays === 7` switches the facet off, and
   * below that a capped 7 fails every comparison the true 10 would have failed.
   *
   * The DENSEST phase rather than the first, which is also the right answer for `at most N days`:
   * a plan carrying one five-day phase is not a three-day plan.
   */
  readonly daysInCycle: number | null;
  /**
   * How many exercise slots the plan prescribes across every phase. Zero is a real answer.
   *
   * Read by the inspector's hero, which is its only consumer since D26 took it off the row's
   * sub-line. It stays on the projection because the hero's KPI is fed from the LIST ROW — that is
   * what lets the plate paint complete on its first frame instead of waiting for `/full`.
   */
  readonly exerciseCount: number;
  /** Live assignments only. Zero is a real answer. */
  readonly assignedClientCount: number;
  /**
   * What last happened to the plan and when — an edit if there was one, otherwise its creation.
   * `null` only when the wire carried neither timestamp. See the header for why this is a pair
   * rather than a date.
   */
  readonly activity: PlanActivity | null;
}

/**
 * The four figures every span-and-rhythm decision reads, with no wire shape attached.
 *
 * **Two endpoints carry the same plan.** `GET /plan-template/library` sends these as scalars it
 * computed in SQL; `GET /plan-template/{id}/full` sends the phase tree they were computed FROM, and
 * the inspector has to state the same three sentences about a client's plan, whose list row is an
 * assignment and carries none of them. So the decisions below take the figures rather than either
 * response, and `inspectorView` derives them from the tree — one rule, two readings, and no way for
 * the library row and the hero beside it to disagree about what `8 tygodni` means.
 */
export interface PlanShape {
  readonly phaseCount: number;
  /** Whether the plan is on the weekday axis. See `isWeekly` for why this is not "not self-paced". */
  readonly weekly: boolean;
  readonly totalCycleCount: number;
  /** Training days in the repeat unit. See `daysInCycle` on the row. */
  readonly daysInCycle: number;
}

/**
 * The wire sends `"DateBased"` as a PascalCase string, and its enum has no other member today.
 * Anything else — a new mode, a null, a casing change — is treated as NOT week-shaped, which is the
 * conservative direction: only a mode this app knows has a weekday axis may put a figure under a
 * weekly cadence.
 */
function isWeekly(schedulingMode: string | null | undefined): boolean {
  return schedulingModeIs(schedulingMode, 'date');
}

/**
 * The wire's other named mode, matched exactly and alone. `'SelfPaced'` is the backend's own
 * PascalCase enum name (`self-paced-plan-requirements.md` R1), and it is spelled here rather than
 * imported from the generated client because the contract types `schedulingMode` as a bare string —
 * there is no generated enum to narrow against.
 *
 * Exported on its second consumer: `clientPlanRow` projects the same field off the assignment, and
 * this is the rule documented to change the day the backend names a third mode. Two scopes on one
 * screen spelling that rule twice would disagree about the same plan the day it does.
 */
export function schedulingMarkerOf(
  schedulingMode: string | null | undefined,
): PlanSchedulingMarker | null {
  return schedulingMode === 'SelfPaced' ? 'selfPaced' : null;
}

/**
 * Trim to a value, or `null`. An empty or whitespace-only string is an absence, not a name.
 *
 * The slice's one trim: `clientPlanRow` and `templateFollowers` both held a byte-identical copy.
 */
export function textOrNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

/**
 * The trainer's word for a category — "Mobilność", never the wire's `"Flexibility"`.
 *
 * Takes the raw category rather than a wire item, so the assignment projection can ask the same
 * question of the same rule: both endpoints send the category as a bare string.
 */
export function categoryLabel(
  category: string | null | undefined,
  key: PlanCategoryKey | null,
  translate: TranslateKey,
): string | null {
  // A category this app has no label for is shown as the server spelled it. That is legible and
  // searchable; a blank cell would lose information the backend actually sent.
  if (key === null) return textOrNull(category);
  return translate(`plans.category.${key}`);
}

/**
 * The event ŚWIEŻOŚĆ dates. An edit wins over a creation, and the fallback is NAMED rather than
 * silent — see the header for the two ways this was previously wrong.
 */
export function planActivityOf(
  updatedAt: string | null | undefined,
  createdAt: string | null | undefined,
): PlanActivity | null {
  const edited = textOrNull(updatedAt);
  if (edited !== null) return { kind: 'edited', at: edited };
  const created = textOrNull(createdAt);
  if (created !== null) return { kind: 'created', at: created };
  return null;
}

/**
 * A plan's span: a finite number of weeks, or a loop.
 *
 * **The guard is the RHYTHM's, not the marker's, and the two answer opposite questions.** A weekly
 * plan's cycle is a week, so its `totalCycleCount` is a count of weeks and can be printed as one.
 * Every other mode — including one this build has never heard of — repeats a cycle that is not a
 * week, so a week count would be a unit this row cannot verify. It reads as a loop instead, which
 * is true of all of them.
 *
 * A zero or absent cycle count on a weekly plan is `null` rather than `0 tygodni`: the backend
 * counts cycles across phases, so zero means the read carried no phases, and that is an absence.
 */
export function planSpanOf(shape: PlanShape): PlanSpan | null {
  if (shape.phaseCount <= 0) return null;
  if (!shape.weekly) return { kind: 'looped' };
  return shape.totalCycleCount > 0 ? { kind: 'weeks', weeks: shape.totalCycleCount } : null;
}

export function planRhythmOf(shape: PlanShape): PlanRhythm | null {
  // The guard is on phases: every figure below is summed or maxed ACROSS phases, so a plan with none
  // cannot have a meaningful cadence, while a plan with phases and a zero count is mid-authoring and
  // worth showing as it stands.
  if (shape.phaseCount <= 0) return null;
  if (shape.weekly) {
    return shape.daysInCycle > 0 ? { kind: 'week', daysPerWeek: shape.daysInCycle } : null;
  }
  return { kind: 'cycle', cycles: shape.totalCycleCount };
}

/**
 * Project one wire item.
 *
 * Exported for the test, which reaches the decisions through here rather than through the helpers
 * above — those are implementation and may be reshaped without the requirement moving.
 */
export function toPlanLibraryRow(
  item: PlanTemplateListItem,
  translate: TranslateKey,
): PlanLibraryRow {
  const categoryKey = planCategoryKey(item.category);
  // A nameless plan still has to be clickable and still has to sort somewhere, so the empty string
  // is kept rather than turned into placeholder copy the model has no business writing. It is also
  // the cover's SEED — the same one `reassignCoverForCategory` folds — so it is read once here
  // rather than twice below.
  const name = textOrNull(item.name) ?? '';
  const category = categoryLabel(item.category, categoryKey, translate);
  const shape: PlanShape = {
    phaseCount: item.phaseCount ?? 0,
    weekly: isWeekly(item.schedulingMode),
    totalCycleCount: item.totalCycleCount ?? 0,
    daysInCycle: item.daysPerWeek ?? 0,
  };
  return {
    id: item.id ?? 0,
    name,
    categoryKey,
    category,
    // Search runs over the name and the trainer's own word for the category, so typing "mobilność"
    // finds the same rows the Mobilność chip does.
    searchText: searchTextOf(name, category),
    ...planCoverPair(item.coverImageUrl, categoryKey, name),
    rhythm: planRhythmOf(shape),
    span: planSpanOf(shape),
    schedulingMarker: schedulingMarkerOf(item.schedulingMode),
    daysInCycle: shape.phaseCount > 0 ? shape.daysInCycle : null,
    exerciseCount: item.exerciseCount ?? 0,
    assignedClientCount: item.assignedClientCount ?? 0,
    activity: planActivityOf(item.updatedAt, item.createdAt),
  };
}

/**
 * The slice's read entry point: the whole response in, rows out.
 *
 * Rows whose `id` the backend did not send are dropped rather than rendered with `id: 0`. A row keyed
 * on zero would collide with every other such row in React's reconciler and would link to
 * `/plans/0`, which 404s — the same class of defect `pages/clients` records for invitations arriving
 * with `Id = 0`, caught here before it can happen rather than after.
 */
export function toPlanLibraryRows(
  items: readonly PlanTemplateListItem[] | undefined,
  translate: TranslateKey,
): PlanLibraryRow[] {
  if (items === undefined) return [];
  return items
    .filter((item) => (item.id ?? 0) > 0)
    .map((item) => toPlanLibraryRow(item, translate));
}
